import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  ChevronLeft,
  ChevronRight,
  FileSpreadsheet,
  FilePlus,
  Search,
  Settings2,
  BriefcaseBusiness,
  Wallet,
  Trash2,
  Presentation,
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  AdminPageHeader,
  adminPageStackClass,
  adminTabsListProps,
  devisQuoteTabsScrollClass,
} from './AdminChrome';
import DevisSalairesTab from './DevisSalairesTab';
import DevisOperationsTab from './DevisOperationsTab';
import DevisQuoteEditor from './DevisQuoteEditor';
import {
  computeDevisTotalAnnuelFraisFixes,
  normalizeOperationCategories,
  todayYmdLocal,
} from './devisQuoteState';

const stripAccents = (s) =>
  String(s)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const fmtEur = (n) =>
  new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(
    Number.isFinite(n) ? n : 0
  );

const PAGE_SIZE_OPTIONS = [25, 50, 100];

const DevisPage = ({ theme, token, apiBase, access, onUnauthorized }) => {
  const [subTab, setSubTab] = useState('devis');
  const [parametresTab, setParametresTab] = useState('operations');
  const [activeQuoteId, setActiveQuoteId] = useState(null);
  const [quotesList, setQuotesList] = useState([]);
  const [listQualifications, setListQualifications] = useState([]);
  const [listOperationCategories, setListOperationCategories] = useState([]);
  const [quotesLoading, setQuotesLoading] = useState(false);
  const [creatingQuote, setCreatingQuote] = useState(false);
  const [listDeleteTarget, setListDeleteTarget] = useState(null);
  const [listDeleting, setListDeleting] = useState(false);
  const [pptxTemplateStatus, setPptxTemplateStatus] = useState(null);
  const [pptxTemplateLoading, setPptxTemplateLoading] = useState(false);
  const [pptxUploading, setPptxUploading] = useState(false);
  const pptxFileInputRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);

  const canConfigure =
    access?.is_super_admin || (access?.permissions || []).includes('devis.configure');

  const canEditDevis =
    access?.is_super_admin || (access?.permissions || []).includes('devis.edit');

  const parametresActive = subTab === 'parametres';

  const operationsActive = parametresActive && parametresTab === 'operations';
  const salairesActive = parametresActive && parametresTab === 'salaires';
  const offrePptxActive = parametresActive && parametresTab === 'offre';
  const devisListActive = subTab === 'devis' && !activeQuoteId;

  const loadQuotes = useCallback(async () => {
    if (!token) return;
    setQuotesLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [quotesRes, qualRes, opCatRes] = await Promise.all([
        axios.get(`${apiBase}/quotes`, { headers }),
        axios.get(`${apiBase}/qualifications`, { headers }),
        axios.get(`${apiBase}/operation-categories`, { headers }),
      ]);
      setQuotesList(Array.isArray(quotesRes.data) ? quotesRes.data : []);
      setListQualifications(Array.isArray(qualRes.data) ? qualRes.data : []);
      setListOperationCategories(normalizeOperationCategories(opCatRes.data));
    } catch (e) {
      if (e.response?.status === 401) onUnauthorized?.();
    } finally {
      setQuotesLoading(false);
    }
  }, [apiBase, token, onUnauthorized]);

  useEffect(() => {
    if (devisListActive) loadQuotes();
  }, [devisListActive, loadQuotes]);

  const loadPptxTemplateStatus = useCallback(async () => {
    if (!token) return;
    setPptxTemplateLoading(true);
    try {
      const { data } = await axios.get(`${apiBase}/offre-template`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPptxTemplateStatus(data && typeof data === 'object' ? data : null);
    } catch (e) {
      if (e.response?.status === 401) onUnauthorized?.();
      else {
        setPptxTemplateStatus(null);
        toast.error('Impossible de charger l’état de la trame PowerPoint.');
      }
    } finally {
      setPptxTemplateLoading(false);
    }
  }, [apiBase, token, onUnauthorized]);

  useEffect(() => {
    if (offrePptxActive) void loadPptxTemplateStatus();
  }, [offrePptxActive, loadPptxTemplateStatus]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter, pageSize]);

  const quotesWithTotals = useMemo(() => {
    return quotesList.map((q) => ({
      ...q,
      totalAnnuelFraisFixes: computeDevisTotalAnnuelFraisFixes(
        q.devis_ui_state,
        listQualifications,
        listOperationCategories
      ),
    }));
  }, [quotesList, listQualifications, listOperationCategories]);

  const filteredQuotes = useMemo(() => {
    const qn = stripAccents(searchQuery);
    const statusSortKey = (q) => (q.status === 'finalized' ? 1 : 0);

    const filtered = quotesWithTotals.filter((q) => {
      const st = q.status === 'finalized' ? 'finalized' : 'draft';
      if (statusFilter === 'draft' && st !== 'draft') return false;
      if (statusFilter === 'finalized' && st !== 'finalized') return false;
      if (!qn) return true;
      const title = stripAccents(q.title || '');
      const company = stripAccents(q.client_company || '');
      const dateStr = stripAccents(String(q.quote_date || ''));
      const priceFr = stripAccents(fmtEur(q.totalAnnuelFraisFixes));
      const priceRaw = stripAccents(String(q.totalAnnuelFraisFixes));
      const priceInt = stripAccents(String(Math.round(q.totalAnnuelFraisFixes)));
      if (
        title.includes(qn) ||
        company.includes(qn) ||
        dateStr.includes(qn) ||
        priceFr.includes(qn) ||
        priceRaw.includes(qn) ||
        priceInt.includes(qn)
      ) {
        return true;
      }
      const digits = qn.replace(/\D/g, '');
      if (digits.length >= 2) {
        const rounded = Math.round(q.totalAnnuelFraisFixes);
        const withCents = Math.round(q.totalAnnuelFraisFixes * 100) / 100;
        if (String(rounded).includes(digits) || String(withCents).replace('.', '').includes(digits)) {
          return true;
        }
      }
      return false;
    });

    // Brouillons en tête, finalisés en fin de liste (pagination incluse)
    return [...filtered].sort((a, b) => statusSortKey(a) - statusSortKey(b));
  }, [quotesWithTotals, searchQuery, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filteredQuotes.length / pageSize) || 1);
  const safePage = Math.min(currentPage, totalPages);
  const pageStart = (safePage - 1) * pageSize;
  const paginatedQuotes = filteredQuotes.slice(pageStart, pageStart + pageSize);
  const rangeFrom = filteredQuotes.length === 0 ? 0 : pageStart + 1;
  const rangeTo = Math.min(pageStart + pageSize, filteredQuotes.length);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  const handleCreateQuote = async () => {
    setCreatingQuote(true);
    try {
      const { data } = await axios.post(
        `${apiBase}/quotes`,
        {
          title: 'Nouveau devis',
          quote_date: todayYmdLocal(),
          client_company: '',
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data?.id) setActiveQuoteId(data.id);
      loadQuotes();
    } catch (e) {
      if (e.response?.status === 401) onUnauthorized?.();
    } finally {
      setCreatingQuote(false);
    }
  };

  const handleSubTabChange = (value) => {
    if (value === 'devis') setActiveQuoteId(null);
    setSubTab(value);
  };

  const fmtPptxUpdated = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('fr-FR', {
        dateStyle: 'short',
        timeStyle: 'short',
      });
    } catch {
      return '—';
    }
  };

  const uploadOffrePptx = async (file) => {
    if (!token || !file) return;
    setPptxUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${apiBase}/offre-template`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Trame PowerPoint enregistrée. L’ancienne a été remplacée.');
      await loadPptxTemplateStatus();
    } catch (e) {
      if (e.response?.status === 401) onUnauthorized?.();
      else {
        const d = e.response?.data?.detail;
        let msg = 'Échec de l’envoi du fichier.';
        if (typeof d === 'string') msg = d;
        else if (Array.isArray(d) && d[0]?.msg) msg = String(d[0].msg);
        toast.error(msg);
      }
    } finally {
      setPptxUploading(false);
    }
  };

  const onOffrePptxFileChange = (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    const lower = f.name.toLowerCase();
    if (!lower.endsWith('.pptx')) {
      toast.error('Seuls les fichiers PowerPoint (.pptx) sont acceptés.');
      return;
    }
    void uploadOffrePptx(f);
  };

  const confirmDeleteFromList = async () => {
    if (!listDeleteTarget?.id || !token) return;
    setListDeleting(true);
    try {
      await axios.delete(`${apiBase}/quotes/${listDeleteTarget.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Devis supprimé.');
      setListDeleteTarget(null);
      loadQuotes();
    } catch (e) {
      if (e.response?.status === 401) onUnauthorized?.();
      else toast.error('Impossible de supprimer le devis.');
    } finally {
      setListDeleting(false);
    }
  };

  return (
    <div className={adminPageStackClass}>
      <AdminPageHeader
        theme={theme}
        icon={FileSpreadsheet}
        title="Devis"
        subtitle="Création de devis et réglages du module."
      />

      <Tabs value={subTab} onValueChange={handleSubTabChange} className="w-full min-w-0">
        <TabsList
          {...adminTabsListProps(
            theme,
            'grid w-full grid-cols-2 h-auto min-h-[52px] gap-2 p-2 sm:min-h-0'
          )}
        >
          <TabsTrigger
            value="devis"
            className="gap-2 py-3 text-sm font-semibold touch-manipulation sm:py-2.5"
            onClick={() => {
              if (subTab === 'devis' && activeQuoteId) setActiveQuoteId(null);
            }}
          >
            <FilePlus className="h-5 w-5 shrink-0 opacity-80 sm:h-4 sm:w-4" aria-hidden />
            Devis
          </TabsTrigger>
          <TabsTrigger
            value="parametres"
            className="gap-2 py-3 text-sm font-semibold touch-manipulation sm:py-2.5"
          >
            <Settings2 className="h-5 w-5 shrink-0 opacity-80 sm:h-4 sm:w-4" aria-hidden />
            Paramètres
          </TabsTrigger>
        </TabsList>

        <TabsContent value="devis" className="mt-5 min-w-0 sm:mt-6">
          {activeQuoteId ? (
            <DevisQuoteEditor
              quoteId={activeQuoteId}
              token={token}
              apiBase={apiBase}
              theme={theme}
              canEditQuotes={canEditDevis}
              onUnauthorized={onUnauthorized}
              onBack={() => setActiveQuoteId(null)}
              onDeleted={() => {
                setActiveQuoteId(null);
                loadQuotes();
              }}
              onQuoteMetadataChange={loadQuotes}
            />
          ) : (
            <div className="space-y-5 sm:space-y-6">
              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                {canEditDevis && (
                  <Button
                    type="button"
                    size="lg"
                    className="h-12 min-h-[48px] w-full gap-2 rounded-xl text-base font-semibold shadow-md touch-manipulation sm:h-11 sm:w-auto sm:min-w-[200px]"
                    style={{ backgroundColor: theme.accent, color: '#fff' }}
                    disabled={creatingQuote}
                    onClick={handleCreateQuote}
                  >
                    <FilePlus className="h-5 w-5 shrink-0" aria-hidden />
                    {creatingQuote ? 'Création…' : 'Créer un devis'}
                  </Button>
                )}
                {!canEditDevis && quotesList.length > 0 && (
                  <p className="text-sm leading-relaxed" style={{ color: theme.textSecondary }}>
                    Vous consultez les devis en <span className="font-semibold" style={{ color: theme.text }}>lecture seule</span>
                    {' '}(permission « Créer et modifier des devis » désactivée).
                  </p>
                )}
              </div>
              <div className="space-y-4">
                <div className="flex flex-col gap-1">
                  <p className="text-xs font-bold uppercase tracking-wide sm:text-sm" style={{ color: theme.textSecondary }}>
                    Devis enregistrés
                  </p>
                  {!quotesLoading && quotesList.length > 0 && (
                    <p className="text-xs" style={{ color: theme.textSecondary }}>
                      {quotesList.length} devis au total · recherche et filtres sur l’ensemble
                    </p>
                  )}
                </div>

                {quotesLoading && (
                  <p className="text-sm" style={{ color: theme.textSecondary }}>
                    Chargement…
                  </p>
                )}

                {!quotesLoading && quotesList.length === 0 && (
                  <p
                    className="rounded-xl border px-4 py-6 text-center text-sm leading-relaxed"
                    style={{ borderColor: theme.border, backgroundColor: theme.panelInset, color: theme.textSecondary }}
                  >
                    Aucun devis pour l’instant.
                  </p>
                )}

                {!quotesLoading && quotesList.length > 0 && (
                  <>
                    <div className="flex flex-col gap-3 rounded-2xl border-2 p-4 shadow-sm sm:flex-row sm:flex-wrap sm:items-end sm:gap-4" style={{ borderColor: theme.border, backgroundColor: theme.panelInset }}>
                      <div className="min-w-0 flex-1 space-y-1.5">
                        <Label htmlFor="devis-search" className="text-xs font-medium" style={{ color: theme.textSecondary }}>
                          Recherche
                        </Label>
                        <div className="relative">
                          <Search
                            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 opacity-50"
                            aria-hidden
                          />
                          <Input
                            id="devis-search"
                            type="search"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Nom, entreprise, date, montant…"
                            className="h-11 min-h-[44px] rounded-xl border-2 pl-10 text-base sm:h-10 sm:text-sm"
                            style={{
                              borderColor: theme.border,
                              backgroundColor: theme.inputBackground,
                              color: theme.text,
                            }}
                            autoComplete="off"
                          />
                        </div>
                      </div>
                      <div className="w-full sm:w-auto sm:min-w-[11rem]">
                        <div className="space-y-1.5">
                          <Label htmlFor="devis-filter-status" className="text-xs font-medium" style={{ color: theme.textSecondary }}>
                            Statut
                          </Label>
                          <select
                            id="devis-filter-status"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="h-11 min-h-[44px] w-full rounded-xl border-2 px-3 text-sm touch-manipulation sm:h-10 sm:min-w-[11rem]"
                            style={{
                              borderColor: theme.border,
                              backgroundColor: theme.inputBackground,
                              color: theme.text,
                            }}
                          >
                            <option value="all">Tous</option>
                            <option value="draft">Brouillon</option>
                            <option value="finalized">Finalisé</option>
                          </select>
                        </div>
                      </div>
                    </div>

                    {filteredQuotes.length === 0 ? (
                      <p
                        className="rounded-xl border px-4 py-6 text-center text-sm leading-relaxed"
                        style={{ borderColor: theme.border, backgroundColor: theme.panelInset, color: theme.textSecondary }}
                      >
                        Aucun devis ne correspond à la recherche ou au filtre.
                      </p>
                    ) : (
                      <>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                          {paginatedQuotes.map((q) => {
                            const isFinalized = q.status === 'finalized';
                            const statusLabel =
                              q.status === 'finalized' ? 'Finalisé' : q.status === 'draft' ? 'Brouillon' : q.status || '—';
                            return (
                              <div
                                key={q.id}
                                className="relative flex flex-col rounded-2xl border-2 p-4 pt-3 shadow-sm transition-shadow hover:shadow-md"
                                style={
                                  isFinalized
                                    ? {
                                        borderColor: '#16a34a',
                                        backgroundColor: 'rgba(22, 163, 74, 0.09)',
                                        boxShadow: '0 0 0 1px rgba(22, 163, 74, 0.22)',
                                      }
                                    : { borderColor: theme.border, backgroundColor: theme.surface }
                                }
                              >
                                {canEditDevis && (
                                  <button
                                    type="button"
                                    className="absolute right-2 top-2 flex h-10 w-10 items-center justify-center rounded-lg text-destructive transition-colors hover:bg-red-500/10 touch-manipulation"
                                    aria-label={`Supprimer ${q.title || 'ce devis'}`}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setListDeleteTarget({ id: q.id, title: q.title });
                                    }}
                                  >
                                    <Trash2 className="h-5 w-5" />
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="min-w-0 flex-1 touch-manipulation rounded-xl px-1 py-1 text-left transition-colors active:bg-black/[0.04] dark:active:bg-white/[0.06]"
                                  onClick={() => setActiveQuoteId(q.id)}
                                >
                                  <dl className={cn('space-y-2.5 text-sm', canEditDevis ? 'pr-10' : 'pr-2')}>
                                    <div>
                                      <dt className="text-[10px] font-bold uppercase tracking-wide" style={{ color: theme.textSecondary }}>
                                        Nom
                                      </dt>
                                      <dd className="mt-0.5 font-semibold leading-snug" style={{ color: theme.text }}>
                                        {q.title?.trim() || 'Sans titre'}
                                      </dd>
                                    </div>
                                    <div>
                                      <dt className="text-[10px] font-bold uppercase tracking-wide" style={{ color: theme.textSecondary }}>
                                        Entreprise
                                      </dt>
                                      <dd
                                        className="mt-0.5 break-words leading-snug"
                                        style={{ color: theme.text }}
                                      >
                                        {q.client_company?.trim() || '—'}
                                      </dd>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                      <div>
                                        <dt className="text-[10px] font-bold uppercase tracking-wide" style={{ color: theme.textSecondary }}>
                                          Date
                                        </dt>
                                        <dd className="mt-0.5 tabular-nums leading-snug" style={{ color: theme.text }}>
                                          {q.quote_date || '—'}
                                        </dd>
                                      </div>
                                      <div>
                                        <dt className="text-[10px] font-bold uppercase tracking-wide" style={{ color: theme.textSecondary }}>
                                          Statut
                                        </dt>
                                        <dd className="mt-0.5 font-medium leading-snug" style={{ color: theme.text }}>
                                          {statusLabel}
                                        </dd>
                                      </div>
                                    </div>
                                    <div>
                                      <dt className="text-[10px] font-bold uppercase tracking-wide" style={{ color: theme.textSecondary }}>
                                        Total annuel (frais fixes)
                                      </dt>
                                      <dd className="mt-0.5 text-base font-bold tabular-nums leading-snug" style={{ color: theme.text }}>
                                        {fmtEur(q.totalAnnuelFraisFixes)} HT
                                      </dd>
                                    </div>
                                  </dl>
                                </button>
                              </div>
                            );
                          })}
                        </div>

                        <nav
                          className="flex flex-col gap-4 rounded-2xl border-2 px-4 py-4 shadow-sm sm:flex-row sm:items-stretch sm:justify-between sm:gap-6"
                          style={{ borderColor: theme.border, backgroundColor: theme.surface }}
                          aria-label="Pagination de la liste des devis"
                        >
                          <p className="text-xs leading-relaxed tabular-nums sm:max-w-[min(100%,24rem)] sm:self-center sm:text-sm" style={{ color: theme.textSecondary }}>
                            Affichage <span className="font-medium" style={{ color: theme.text }}>{rangeFrom}–{rangeTo}</span> sur{' '}
                            <span className="font-medium" style={{ color: theme.text }}>{filteredQuotes.length}</span> devis
                            {filteredQuotes.length !== quotesList.length && (
                              <span className="block sm:mt-0.5">({quotesList.length} enregistrés au total)</span>
                            )}
                          </p>
                          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                            <div
                              className="flex min-h-[44px] items-center gap-2 rounded-xl border-2 px-3 py-1.5 sm:min-h-0 sm:py-1"
                              style={{ borderColor: theme.border, backgroundColor: theme.panelInset }}
                            >
                              <Label htmlFor="devis-page-size" className="mb-0 shrink-0 text-xs font-medium whitespace-nowrap" style={{ color: theme.textSecondary }}>
                                Par page
                              </Label>
                              <select
                                id="devis-page-size"
                                value={String(pageSize)}
                                onChange={(e) => setPageSize(Number(e.target.value))}
                                className="min-h-[36px] min-w-0 flex-1 cursor-pointer rounded-lg border px-2 py-1.5 text-sm touch-manipulation sm:min-h-0 sm:min-w-[7.5rem]"
                                style={{
                                  borderColor: theme.border,
                                  backgroundColor: theme.inputBackground,
                                  color: theme.text,
                                }}
                                aria-label="Nombre de devis par page"
                              >
                                {PAGE_SIZE_OPTIONS.map((n) => (
                                  <option key={n} value={String(n)}>
                                    {n}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div
                              className="inline-flex min-h-[44px] w-full overflow-hidden rounded-xl border-2 sm:min-h-0 sm:w-auto"
                              style={{ borderColor: theme.border, backgroundColor: theme.panelInset }}
                            >
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-auto min-h-[44px] flex-1 gap-1 rounded-none border-0 px-3 py-2.5 shadow-none hover:bg-black/[0.06] dark:hover:bg-white/[0.08] sm:h-9 sm:min-h-0 sm:flex-none sm:px-3"
                                style={{ color: theme.text }}
                                disabled={safePage <= 1}
                                aria-label="Page précédente"
                                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                              >
                                <ChevronLeft className="h-4 w-4 shrink-0" aria-hidden />
                                <span className="hidden sm:inline">Précédent</span>
                              </Button>
                              <div
                                className="flex min-w-[5.5rem] flex-1 items-center justify-center border-x px-2 py-2 text-xs font-semibold tabular-nums sm:min-w-[6.5rem] sm:flex-none sm:px-4 sm:text-sm"
                                style={{ borderColor: theme.border, color: theme.text }}
                                aria-live="polite"
                              >
                                Page {safePage} / {totalPages}
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="h-auto min-h-[44px] flex-1 gap-1 rounded-none border-0 px-3 py-2.5 shadow-none hover:bg-black/[0.06] dark:hover:bg-white/[0.08] sm:h-9 sm:min-h-0 sm:flex-none sm:px-3"
                                style={{ color: theme.text }}
                                disabled={safePage >= totalPages}
                                aria-label="Page suivante"
                                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                              >
                                <span className="hidden sm:inline">Suivant</span>
                                <ChevronRight className="h-4 w-4 shrink-0" aria-hidden />
                              </Button>
                            </div>
                          </div>
                        </nav>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="parametres" className="mt-5 min-w-0 sm:mt-6">
          <Tabs value={parametresTab} onValueChange={setParametresTab} className="w-full min-w-0">
            <div className={devisQuoteTabsScrollClass}>
              <TabsList
                {...adminTabsListProps(
                  theme,
                  'inline-flex w-max min-w-full flex-nowrap gap-2 p-2 sm:grid sm:w-full sm:grid-cols-3 sm:gap-2'
                )}
              >
                <TabsTrigger
                  value="operations"
                  className="min-h-[48px] gap-2 px-4 py-3 text-sm font-semibold touch-manipulation sm:min-h-0 sm:py-2.5"
                >
                  <BriefcaseBusiness className="h-5 w-5 shrink-0 opacity-80 sm:h-4 sm:w-4" aria-hidden />
                  Opérations
                </TabsTrigger>
                <TabsTrigger
                  value="salaires"
                  className="min-h-[48px] gap-2 px-4 py-3 text-sm font-semibold touch-manipulation sm:min-h-0 sm:py-2.5"
                >
                  <Wallet className="h-5 w-5 shrink-0 opacity-80 sm:h-4 sm:w-4" aria-hidden />
                  Salaires
                </TabsTrigger>
                <TabsTrigger
                  value="offre"
                  className="min-h-[48px] gap-2 px-4 py-3 text-sm font-semibold touch-manipulation sm:min-h-0 sm:py-2.5"
                >
                  <Presentation className="h-5 w-5 shrink-0 opacity-80 sm:h-4 sm:w-4" aria-hidden />
                  Trame offre
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="operations" className="mt-4 min-w-0 sm:mt-5">
              <DevisOperationsTab
                isActive={operationsActive}
                token={token}
                apiBase={apiBase}
                theme={theme}
                onUnauthorized={onUnauthorized}
                canConfigure={canConfigure}
              />
            </TabsContent>

            <TabsContent value="salaires" className="mt-4 min-w-0 sm:mt-5">
              <DevisSalairesTab
                isActive={salairesActive}
                token={token}
                apiBase={apiBase}
                theme={theme}
                onUnauthorized={onUnauthorized}
                canConfigure={canConfigure}
              />
            </TabsContent>

            <TabsContent value="offre" className="mt-4 min-w-0 sm:mt-5">
              <div
                className="space-y-5 rounded-2xl border-2 p-4 shadow-sm sm:p-6"
                style={{ borderColor: theme.border, backgroundColor: theme.panelInset }}
              >
                <div className="space-y-2">
                  <p className="text-sm font-semibold" style={{ color: theme.text }}>
                    Trame PowerPoint pour l’export offre
                  </p>
                  <p className="text-sm leading-relaxed" style={{ color: theme.textSecondary }}>
                    Ce modèle sert à générer la présentation à partir d’un devis. Seul le format PowerPoint
                    (.pptx) est accepté. Chaque nouvel envoi supprime la trame précédemment enregistrée sur ce
                    serveur et la remplace.
                  </p>
                </div>

                {pptxTemplateLoading && (
                  <p className="text-sm" style={{ color: theme.textSecondary }}>
                    Chargement…
                  </p>
                )}

                {!pptxTemplateLoading && pptxTemplateStatus && (
                  <div
                    className="space-y-3 rounded-xl border px-4 py-3 text-sm"
                    style={{ borderColor: theme.border, backgroundColor: theme.surface }}
                  >
                    <div className="grid gap-2 sm:grid-cols-[minmax(0,7.5rem)_1fr] sm:gap-x-4">
                      <span className="font-medium shrink-0" style={{ color: theme.textSecondary }}>
                        Source active
                      </span>
                      <span style={{ color: theme.text }}>
                        {pptxTemplateStatus.source === 'env' && 'Variable d’environnement CHIFFRAGE_PPTX_TEMPLATE'}
                        {pptxTemplateStatus.source === 'stored' && 'Fichier enregistré sur le serveur (upload)'}
                        {pptxTemplateStatus.source === 'repo' && 'Fichier fourni avec l’installation / le dépôt'}
                        {pptxTemplateStatus.source === 'none' && 'Aucune trame — l’export utilisera un modèle minimal'}
                      </span>
                      {pptxTemplateStatus.filename && (
                        <>
                          <span className="font-medium shrink-0" style={{ color: theme.textSecondary }}>
                            Fichier
                          </span>
                          <span className="min-w-0 break-all" style={{ color: theme.text }}>
                            {pptxTemplateStatus.filename}
                          </span>
                        </>
                      )}
                      <span className="font-medium shrink-0" style={{ color: theme.textSecondary }}>
                        Dernière modification
                      </span>
                      <span style={{ color: theme.text }}>{fmtPptxUpdated(pptxTemplateStatus.updated_at)}</span>
                    </div>
                  </div>
                )}

                {!pptxTemplateLoading && !pptxTemplateStatus && (
                  <p className="text-sm" style={{ color: theme.textSecondary }}>
                    État de la trame indisponible.
                  </p>
                )}

                {pptxTemplateStatus?.source === 'env' && (
                  <p
                    className="rounded-lg border-l-4 px-3 py-2.5 text-sm leading-relaxed"
                    style={{
                      borderLeftColor: theme.accent,
                      backgroundColor: theme.surface,
                      color: theme.text,
                    }}
                  >
                    <span className="font-semibold">Remarque :</span> la variable{' '}
                    <code className="rounded px-1 py-0.5 text-xs" style={{ backgroundColor: theme.panelInset }}>
                      CHIFFRAGE_PPTX_TEMPLATE
                    </code>{' '}
                    est prioritaire sur le fichier envoyé depuis cette page. Tant qu’elle est définie et pointe
                    vers un fichier valide, c’est ce fichier qui sera utilisé pour l’export, pas celui uploadé
                    ici.
                  </p>
                )}

                {pptxTemplateStatus?.source === 'repo' && (
                  <p className="text-sm leading-relaxed" style={{ color: theme.textSecondary }}>
                    La trame actuelle provient du dépôt. Après un envoi réussi depuis cette page, la version sur
                    le serveur prendra le pas sur celle du dépôt pour les exports.
                  </p>
                )}

                {!canConfigure && (
                  <p className="text-sm leading-relaxed" style={{ color: theme.textSecondary }}>
                    La modification de la trame nécessite la permission « Configurer le module devis ».
                  </p>
                )}

                {canConfigure && (
                  <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                    <input
                      ref={pptxFileInputRef}
                      type="file"
                      accept=".pptx,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                      className="sr-only"
                      aria-hidden
                      tabIndex={-1}
                      onChange={onOffrePptxFileChange}
                    />
                    <Button
                      type="button"
                      size="lg"
                      className="h-12 min-h-[48px] w-full gap-2 rounded-xl text-base font-semibold shadow-md touch-manipulation sm:h-11 sm:w-auto sm:min-w-[220px]"
                      style={{ backgroundColor: theme.accent, color: '#fff' }}
                      disabled={pptxUploading}
                      onClick={() => pptxFileInputRef.current?.click()}
                    >
                      <Presentation className="h-5 w-5 shrink-0" aria-hidden />
                      {pptxUploading ? 'Envoi en cours…' : 'Choisir un fichier .pptx'}
                    </Button>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </TabsContent>
      </Tabs>

      <AlertDialog open={!!listDeleteTarget} onOpenChange={(open) => !open && setListDeleteTarget(null)}>
        <AlertDialogContent style={{ borderColor: theme.border, backgroundColor: theme.surface }}>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: theme.text }}>Supprimer ce devis ?</AlertDialogTitle>
            <AlertDialogDescription style={{ color: theme.textSecondary }}>
              « {listDeleteTarget?.title?.trim() || 'Sans titre'} » sera définitivement supprimé.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={listDeleting}
              className="mt-2 border-2 shadow-sm hover:brightness-110 dark:hover:bg-white/10 sm:mt-0"
              style={{
                borderColor: theme.border,
                backgroundColor: theme.panelInset,
                color: theme.text,
              }}
            >
              Annuler
            </AlertDialogCancel>
            <Button
              type="button"
              variant="destructive"
              disabled={listDeleting}
              onClick={() => confirmDeleteFromList()}
            >
              {listDeleting ? 'Suppression…' : 'Supprimer'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DevisPage;
